import { aggregateCounts, type CoverageCounts } from './coverageStatus';
import type { CoverageBarCounts } from './CoverageBar';
import { buildLanguageRollups, type LanguageRollup } from './languageRollups';
import type { TranslationPillSummary } from './useContentTranslations';

// The coverage matrix shape shared by listByCourse and listByUnit: an array of
// unit groups, each with resources carrying their translation rows. Taken from
// buildLanguageRollups so this can't drift from what the rollups walk.
type CoverageUnits = Parameters<typeof buildLanguageRollups>[0];

// Totals are the rollups summed, so a pill and its rows share one walk.
function sumRollups(rollups: readonly LanguageRollup[]): CoverageCounts {
  const counts: CoverageCounts = {
    ready: 0,
    inProgress: 0,
    stale: 0,
    failed: 0,
    notTranslated: 0,
    total: 0,
    withGaps: 0,
  };
  for (const rollup of rollups) {
    counts.ready += rollup.available;
    counts.inProgress += rollup.pending;
    counts.stale += rollup.stale;
    counts.failed += rollup.failed;
    counts.notTranslated += rollup.notTranslated;
    counts.total += rollup.total;
    counts.withGaps += rollup.withGaps;
  }
  return counts;
}

// Most recent activity across every cell, for the popover's "Last updated"
// footer. Matches the resource panel: generatedAt when set, else updatedAt.
function latestActivity(units: CoverageUnits): Date | null {
  let latest: Date | null = null;
  for (const unit of units) {
    for (const resource of unit.resources) {
      for (const row of resource.rows) {
        const at = row.generatedAt ?? row.updatedAt;
        if (at != null && (latest === null || at > latest)) {
          latest = at;
        }
      }
    }
  }
  return latest;
}

function toBarCounts(counts: CoverageCounts): CoverageBarCounts {
  return {
    ready: counts.ready - counts.withGaps,
    gaps: counts.withGaps,
    inProgress: counts.inProgress,
    attention: counts.failed + counts.stale,
    notStarted: counts.notTranslated,
  };
}

// Unlike the shared ladder (where failed outranks in-progress), a still-running
// batch stays blue while some cells have failed, so one early failure doesn't
// freeze the pill red for the whole run. Failure surfaces in the tooltip and
// popover, and wins once nothing is left running.
function coveragePill(counts: CoverageCounts): TranslationPillSummary {
  if (counts.total === 0) {
    return { kind: 'none' };
  }
  if (counts.inProgress > 0) {
    return { kind: 'in-progress', ready: counts.ready, total: counts.total };
  }
  return aggregateCounts(counts);
}

// The batch coverage a toolbar pill renders: the pill glyph, the fraction, the
// bar, and the per-language ledger rows. Shared by the course and unit pills so
// they present identically; each hook layers on its own `isActive` rule.
export type CoverageSummary = {
  pill: TranslationPillSummary;
  counts: CoverageCounts;
  barCounts: CoverageBarCounts;
  // The batch fraction: cells available (partly-in-English included) over total.
  ready: number;
  total: number;
  rollups: LanguageRollup[];
  // Most recent cell activity, for the "Last updated" footer; null before any
  // translation exists.
  lastUpdatedAt: Date | null;
};

// What a toolbar-pill hook returns: the batch coverage plus the query state the
// pill needs. Shared by the course and unit pills; each hook computes `isActive`
// its own way (a configured language set for courses, any published resource for
// units).
export type TranslationsToolbarSummary = CoverageSummary & {
  isLoading: boolean;
  isError: boolean;
  isActive: boolean;
};

export function summariseCoverage(
  units: CoverageUnits,
  supportedLanguages: readonly string[],
  isError: boolean,
): CoverageSummary {
  const { rollups } = buildLanguageRollups(units, supportedLanguages);
  const counts = sumRollups(rollups);
  return {
    pill: isError ? { kind: 'unknown' } : coveragePill(counts),
    counts,
    barCounts: toBarCounts(counts),
    ready: counts.ready,
    total: counts.total,
    rollups,
    lastUpdatedAt: latestActivity(units),
  };
}
